import { Component, h } from 'preact'
import { ArticleType } from './Types'
import UserAvatar from '../User/UserAvatar'
import Phrase from '../../libs/translationsUtils/phrase'
import Content from '../common/Content'
import TopicLike from '../topic-list-view/TopicLike'
import Axios from 'axios'
import LikedUsers, { LikedUserType } from '../topic-list-view/LikedUsers'
import TopicMore from '../topic-list-view/TopicMore'
import Editor from '../editor'
import TopicPublicTags from '../topic-list-view/TopicPublicTags'
import { canUserEditPost } from '../utils/permissions'
import { CONTENT_TYPE_ARTICLE } from '@insided/shared/utils/contentType'
import classNames from 'classnames'

interface Props {
  topic: ArticleType
  currentUser?: LikedUserType
  currentPage: number
  isPreview?: boolean
}

interface State {
  content: string | null
  isCurrentUserLiked: boolean
  tags: string[]
  likedUsers: {
    [key: string]: LikedUserType
  }
  showEditor: boolean
  isLoading: boolean
  hideFirstPost: boolean
  editorErrors: Array<{
    category: string
    phrase: string
  }>
}

class ArticleTopicView extends Component<Props, State> {
  private content: string | null = null
  private readonly canUserEditPost: boolean = canUserEditPost() && !this.props.isPreview

  constructor (props: Props) {
    super(props)
    this.state = {
      content: props.topic.content,
      isCurrentUserLiked: !!this.props.currentUser && this.props.currentUser.id !== null && this.props.topic.likedBy.includes(this.props.currentUser.id.toString()),
      likedUsers: {},
      showEditor: false,
      isLoading: false,
      editorErrors: [],
      hideFirstPost: props.currentPage > 1,
      tags: props.topic.tags
    }
    this.toggleLikeStatus = this.toggleLikeStatus.bind(this)
    this.onChangeOpeningPost = this.onChangeOpeningPost.bind(this)
    this.content = props.topic.content
  }

  async toggleLikeStatus () {
    if (!this.props.currentUser) return

    if (this.props.currentUser.id === null) {
      const event = new CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    if (this.state.isCurrentUserLiked) {
      delete this.state.likedUsers[this.props.currentUser.id]
      this.setState({ likedUsers: this.state.likedUsers, isCurrentUserLiked: false })
      await Axios.post(`/community/api/articles/${this.props.topic.id}/unlike`, {
        unlikedBy: this.props.currentUser.id
      })
    } else {
      this.setState({ likedUsers: { ...this.state.likedUsers, [this.props.currentUser.id]: this.props.currentUser }, isCurrentUserLiked: true })
      await Axios.post(`/community/api/articles/${this.props.topic.id}/like`, {
        likedBy: this.props.currentUser.id
      })
    }
  }

  async onChangeOpeningPost () {
    this.setState({ isLoading: true })
    try {
      await Axios.post(`/community/api/articles/${this.props.topic.id}/editContent`, {
        content: this.content
      })
      this.setState({ showEditor: false, content: this.content, isLoading: false })
    } catch (e) {
      this.setState({ isLoading: false })
      if (e.response?.data?.errors?.content) {
        this.setState({ editorErrors: [{ category: 'Forum', phrase: 'validation.text.required' }] })
      } else {
        this.setState({ editorErrors: [{ category: 'Forum', phrase: 'Something\'s gone wrong' }] })
      }
    }
  }

  async componentDidMount () {
    if (this.props.topic.likedBy.length) {
      const res = await Axios.post('/member/getUsersById', {
        ids: this.props.topic.likedBy
      })

      this.setState({ likedUsers: res.data })
    }
  }

  render () {
    const { topic, currentPage, isPreview } = this.props
    const { content, isCurrentUserLiked, likedUsers, tags, showEditor, isLoading, editorErrors, hideFirstPost } = this.state
    const { author } = topic
    return (
      <div>
        <div className={`post box__pad qa-topic-post-box post--odd js-toggle-target ${hideFirstPost ? 'is-hidden' : ''}`}>
          <div className='post__user__info'>
            <UserAvatar
              user={{ ...author, ...{ name: author.username } }}
              extraClass='avatar avatar--responsive'
              showRank
            />
          </div>
          <div className='post__user__meta'>
            <ul className={classNames('list', 'list--dot-seperated', 'text--meta')}>
              <li>
                <div className='group--inline'>
                  <a href={`/member/profile?mid=${author.id}`}>{author.username}</a>
                </div>
              </li>
              <li className='is-hidden-S'>
                <Phrase p='topic.meta.post.reply' count={{ n: author.reputation.repliesMade.toString() }} />
              </li>
            </ul>
          </div>
          <div>
            {
              showEditor
                ? (
                  <div className='post__content post__edit'>
                    <Editor
                      data={this.state.content}
                      attachments={[]}
                      canHavePoll={false}
                      isLoading={isLoading}
                      errors={editorErrors}
                      onSubmit={this.onChangeOpeningPost}
                      onCancel={() => this.setState({ showEditor: false })}
                      onChange={(content: string) => {
                        this.content = content
                      }}
                      profile='openingPost'
                    />
                  </div>
                  )
                : (
                  <Content
                    content={content ?? ''}
                    className={classNames('post__content', 'qa-topic-post-content', 'post__content--new-editor')}
                  />
                  )
            }
          </div>
          <TopicPublicTags
            topicId={topic.id}
            contentType={CONTENT_TYPE_ARTICLE}
            tags={tags}
            canUserEdit={this.canUserEditPost}
            onChange={(newTags) => this.setState({ tags: newTags })}
          />
          {
            !isPreview && Object.keys(likedUsers).length
              ? (
                <LikedUsers likedUsers={Object.keys(likedUsers).map(id => likedUsers[id])} previewCount={3} isCurrentUserLiked={isCurrentUserLiked} />
                )
              : null
          }
          {
            !isPreview
              ? <div className={classNames('post__footer', 'post__footer-wrapper', 'qa-topic-post-footer')}>
                <TopicLike className={`${isCurrentUserLiked ? 'active' : ''}`} onClick={this.toggleLikeStatus} />
                {
                this.canUserEditPost
                  ? (
                    <TopicMore>
                      <li onClick={() => this.setState({ showEditor: true })}><Phrase p='Edit' /></li>
                    </TopicMore>
                    )
                  : null
              }
              </div>
              : null
          }
        </div>
        {
          currentPage > 1
            ? (
              <a href='#' className='first-post__close' onClick={() => this.setState({ hideFirstPost: !hideFirstPost })}>
                {
                hideFirstPost ? <Phrase p='showFirstpost.message' /> : <Phrase p='hideFirstpost.message' />
              }
              </a>
              )
            : null
        }
      </div>
    )
  }
}
export default ArticleTopicView
